'use client';

/**
 * Optimized Dynamic Template System
 * Minimal, scalable template loader with automatic theme application
 */

import React, {
  createContext,
  useContext,
  ReactNode,
  useMemo,
  Suspense,
} from 'react';
import { config, getCurrentTemplateName, getCurrentTheme } from '@/config';

// ============================================================================
// TYPES
// ============================================================================

export interface TemplateComponents {
  Template: React.ComponentType<{ children: ReactNode }>;
  Navbar: React.ComponentType;
  Footer: React.ComponentType;
  HomeContent: React.ComponentType;
  TripTypeTab: React.ComponentType;
  TripTypeTabContent: React.ComponentType;
  TripTypeSlider?: React.ComponentType;
}

export interface TemplateConfig {
  name: string;
  displayName: string;
  category: 'default' | 'corporate' | 'branded';
  requiredComponents: string[];
  optionalComponents: string[];
}

// ============================================================================
// TEMPLATE REGISTRY
// ============================================================================

const TEMPLATES: Record<string, TemplateConfig> = {
  template1: {
    name: 'template1',
    displayName: 'Template 1',
    category: 'default',
    requiredComponents: [
      'Template',
      'Navbar',
      'Footer',
      'HomeContent',
      'TripTypeTab',
      'TripTypeTabContent',
    ],
    optionalComponents: ['TripTypeSlider'],
  },
  template2: {
    name: 'template2',
    displayName: 'Template 2',
    category: 'default',
    requiredComponents: [
      'Template',
      'Navbar',
      'Footer',
      'HomeContent',
      'TripTypeTab',
      'TripTypeTabContent',
    ],
    optionalComponents: ['TripTypeSlider'],
  },
  corporate: {
    name: 'corporate',
    displayName: 'Corporate Template',
    category: 'corporate',
    requiredComponents: [
      'Template',
      'Navbar',
      'Footer',
      'HomeContent',
      'TripTypeTab',
      'TripTypeTabContent',
    ],
    optionalComponents: ['TripTypeSlider'],
  },
  flysync: {
    name: 'flysync',
    displayName: 'FlySync Template',
    category: 'branded',
    requiredComponents: [
      'Template',
      'Navbar',
      'Footer',
      'HomeContent',
      'TripTypeTab',
      'TripTypeTabContent',
    ],
    optionalComponents: ['TripTypeSlider'],
  },
  travelOne: {
    name: 'travelOne',
    displayName: 'TravelOne Template',
    category: 'branded',
    requiredComponents: [
      'Template',
      'Navbar',
      'Footer',
      'HomeContent',
      'TripTypeTab',
      'TripTypeTabContent',
    ],
    optionalComponents: ['TripTypeSlider'],
  },
  Helinto: {
    name: 'Helinto',
    displayName: 'Helinto Template',
    category: 'branded',
    requiredComponents: [
      'Template',
      'Navbar',
      'Footer',
      'HomeContent',
      'TripTypeTab',
      'TripTypeTabContent',
    ],
    optionalComponents: ['TripTypeSlider'],
  },
  makevoyage: {
    name: 'makevoyage',
    displayName: 'MakeVoyage Template',
    category: 'branded',
    requiredComponents: [
      'Template',
      'Navbar',
      'Footer',
      'HomeContent',
      'TripTypeTab',
      'TripTypeTabContent',
    ],
    optionalComponents: ['TripTypeSlider'],
  },
};

const templateModules = import.meta.glob<{ default: React.ComponentType<any> }>(
  './*/*.tsx',
);

// ============================================================================
// UTILITIES
// ============================================================================

export const getTemplateConfig = (templateName?: string): TemplateConfig => {
  const name = templateName || getCurrentTemplateName();
  return TEMPLATES[name] || TEMPLATES.template1;
};

// ============================================================================
// CONTEXT
// ============================================================================

interface TemplateContextType {
  template: TemplateConfig;
  components: TemplateComponents;
  theme: any;
}

const TemplateContext = createContext<TemplateContextType | null>(null);

export const useTemplate = () => {
  const context = useContext(TemplateContext);
  if (!context) {
    throw new Error('useTemplate must be used within a TemplateProvider');
  }
  return context;
};

export const useTemplateTheme = () => {
  const { theme } = useTemplate();
  return theme;
};

// ============================================================================
// COMPONENT LOADER
// ============================================================================

const createComponentLoader = (
  templateName: string,
  componentName: string,
  isRequired: boolean = true,
) => {
  return React.lazy(async () => {
    try {
      const requestedPath = `./${templateName}/${componentName}.tsx`;
      const requestedLoader = templateModules[requestedPath];

      if (requestedLoader) {
        return requestedLoader();
      }
    } catch (error) {
      console.warn(
        `Failed to load ${componentName} from ${templateName}:`,
        error,
      );
    }

    if (isRequired) {
      try {
        const fallbackPath = `./template1/${componentName}.tsx`;
        const fallbackLoader = templateModules[fallbackPath];

        if (fallbackLoader) {
          return fallbackLoader();
        }
      } catch (fallbackError) {
        console.error(`Failed to load fallback ${componentName}:`, fallbackError);
      }

      return {
        default: () =>
          React.createElement(
            'div',
            {
              className:
                'rounded border border-red-200 bg-red-50 p-4 text-center text-red-500',
            },
            React.createElement(
              'p',
              null,
              `Required component ${componentName} not available`,
            ),
            React.createElement(
              'p',
              { className: 'text-sm' },
              `Template: ${templateName}`,
            ),
          ),
      };
    }

    return {
      default: () =>
        React.createElement(
          'div',
          {
            className: 'p-2 text-center text-gray-400 text-sm',
          },
          `${componentName} not available`,
        ),
    };
  });
};

// ============================================================================
// PROVIDER
// ============================================================================

interface TemplateProviderProps {
  children: ReactNode;
  templateName?: string;
}

export const TemplateProvider: React.FC<TemplateProviderProps> = ({
  children,
  templateName,
}) => {
  const currentTemplateName = templateName || getCurrentTemplateName();
  const template = getTemplateConfig(currentTemplateName);
  const theme = getCurrentTheme();

  // Create components dynamically
  const components = useMemo((): TemplateComponents => {
    const componentLoaders: any = {};

    // Load required components
    template.requiredComponents.forEach((componentName) => {
      componentLoaders[componentName] = createComponentLoader(
        currentTemplateName,
        componentName,
        true,
      );
    });

    // Load optional components
    template.optionalComponents.forEach((componentName) => {
      componentLoaders[componentName] = createComponentLoader(
        currentTemplateName,
        componentName,
        false,
      );
    });

    return componentLoaders;
  }, [currentTemplateName, template]);

  // Simple loading component
  const LoadingComponent = () =>
    React.createElement(
      'div',
      {
        className:
          'flex items-center justify-center min-h-screen bg-gray-50 border-b-1',
      },
      React.createElement(
        'div',
        { className: 'text-center' },
        React.createElement('div', {
          className:
            'animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4',
        }),
        // React.createElement(
        //   'div',
        //   { className: 'text-gray-600 text-lg' },
        //   `<img src="/${config.corporateName}/loader.gif" alt="loading" className="w-12 h-12" />`,
        // ),
      ),
    );

  return React.createElement(
    TemplateContext.Provider,
    { value: { template, components, theme } },
    React.createElement(
      Suspense,
      { fallback: React.createElement(LoadingComponent) },
      children,
    ),
  );
};

// ============================================================================
// COMPONENT WRAPPERS
// ============================================================================

export const TemplateWrapper: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const {
    components: { Template },
  } = useTemplate();
  return React.createElement(Template, null, children);
};

export const NavbarComponent: React.FC = () => {
  const {
    components: { Navbar },
  } = useTemplate();
  return React.createElement(Navbar);
};

export const FooterComponent: React.FC = () => {
  const {
    components: { Footer },
  } = useTemplate();
  return React.createElement(Footer);
};

export const HomeContentComponent: React.FC = () => {
  const {
    components: { HomeContent },
  } = useTemplate();
  return React.createElement(HomeContent);
};

export const TripTypeTabComponent: React.FC = () => {
  const {
    components: { TripTypeTab },
  } = useTemplate();
  return React.createElement(TripTypeTab);
};

export const TripTypeSliderComponent: React.FC = () => {
  const {
    components: { TripTypeSlider },
  } = useTemplate();
  if (!TripTypeSlider) {
    return React.createElement(
      'div',
      { className: 'text-gray-500' },
      'Trip type slider not available',
    );
  }
  return React.createElement(TripTypeSlider);
};

export const TripTypeTabContentComponent: React.FC = () => {
  const {
    components: { TripTypeTabContent },
  } = useTemplate();
  if (!TripTypeTabContent) {
    return React.createElement(
      'div',
      { className: 'text-gray-500' },
      'Trip type tab content not available',
    );
  }
  return React.createElement(TripTypeTabContent);
};

// ============================================================================
// VALIDATION (Optional - only if needed)
// ============================================================================

export const validateTemplate = async (
  templateName?: string,
): Promise<{
  isValid: boolean;
  missingComponents: string[];
  availableComponents: string[];
}> => {
  const name = templateName || getCurrentTemplateName();
  const template = getTemplateConfig(name);
  const missingComponents: string[] = [];
  const availableComponents: string[] = [];

  // Check required components
  for (const componentName of template.requiredComponents) {
    const componentPath = `./${name}/${componentName}.tsx`;
    if (templateModules[componentPath]) {
      availableComponents.push(componentName);
    } else {
      missingComponents.push(componentName);
    }
  }

  // Check optional components
  for (const componentName of template.optionalComponents) {
    const componentPath = `./${name}/${componentName}.tsx`;
    if (templateModules[componentPath]) {
      availableComponents.push(componentName);
    }
  }

  return {
    isValid: missingComponents.length === 0,
    missingComponents,
    availableComponents,
  };
};
